import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Chip, ToggleButton, ToggleButtonGroup, useMediaQuery } from '@mui/material';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../loading-page';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { SearchBar } from '../../presentation-components/display-elements-components/search-bar';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import { ISurvey, ISurveyQuestionGroup } from '../../../shared/services/survey/survey.model';
import { SurveyStatusChip } from '../../presentation-components/display-elements-components/survey-status-chip';
import { CreateSurveyDialog } from '../../dialogs/surveys/create-survey-dialog';
import { segmentedToggleSx } from '../../../shared/utils/segmented-toggle-sx';
import { useDebounce } from '../../../hooks/use-debounce-hook';
import gaiaTheme from '../../../root-components/theme';
import moment from 'moment';

const headCells: IHeadCell[] = [
  { id: 'name', label: 'Name', numeric: false, sortable: true, includeInMobile: true, isHeading: true },
  { id: 'type', label: 'Survey type', numeric: false, sortable: false },
  { id: 'status', label: 'Status', numeric: false, sortable: false, includeInMobile: true },
  { id: 'responses', label: 'Responses', numeric: false, sortable: false, includeInMobile: true },
  { id: 'access', label: 'Access', numeric: false, sortable: true },
  { id: 'group', label: 'Question set', numeric: false, sortable: true },
  { id: 'created', label: 'Created', numeric: false, sortable: false },
];

const ACCESS_LABELS: Record<string, string> = {
  INVITED_ONLY: 'Invite links only',
  OPEN_LINK_ONLY: 'Open link only',
  BOTH: 'Invite links and open link',
};

const STATUS_FILTERS = [
  { value: '', label: 'All', ariaLabel: 'All statuses' },
  { value: 'DRAFT', label: 'Draft', ariaLabel: 'Draft surveys' },
  { value: 'ACTIVE', label: 'Active', ariaLabel: 'Active surveys' },
  { value: 'ARCHIVED', label: 'Archived', ariaLabel: 'Archived surveys' },
];

/** "12 of 30 responded" from the opt-in list counts; open-link surveys have no invite denominator. */
function responsesCell(s: ISurvey): string {
  const counts = s.counts;
  if (!counts) return '—';
  if (s.distributionMode === 'OPEN_LINK_ONLY' || counts.invitationsSent === 0) {
    return counts.submitted > 0 ? `${counts.submitted} responded` : '—';
  }
  return `${counts.submitted} of ${counts.invitationsSent} responded`;
}


export const SurveysListPage = () => {
  const navigate = useNavigate();
  const surveyAdminService = SurveyAdminService.getInstance();
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));

  const [searchParams, setSearchParams] = useSearchParams();
  // Filters live in the URL so they survive navigation and can be linked to.
  // Both are derived from searchParams (not copied on mount) so in-page
  // navigation to e.g. /surveys?status=ACTIVE switches the filter.
  const statusFilter = searchParams.get('status') ?? '';
  const urlSearch = searchParams.get('search') ?? '';

  const [surveys, setSurveys] = useState<ISurvey[]>();
  const [questionGroups, setQuestionGroups] = useState<ISurveyQuestionGroup[]>([]);
  const [searchQuery, setSearchQuery] = useState(urlSearch);
  const [selected, setSelected] = useState<string[]>([]);
  const [openDialog, setOpenDialog] = useState(false);

  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  // Push the debounced search text into the URL. Deliberately keyed on the
  // debounced value only: with searchParams in the deps, an in-page navigation
  // that clears ?search= would re-run this with the stale debounced value and
  // resurrect the cleared search. Navigation wins; the sync effect below then
  // brings the input (and so the debounced value) in line with the URL.
  useEffect(() => {
    const current = searchParams.get('search') ?? '';
    if (debouncedSearchQuery === current) return;
    const next = new URLSearchParams(searchParams);
    if (debouncedSearchQuery) {
      next.set('search', debouncedSearchQuery);
    } else {
      next.delete('search');
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  // Pick up external URL changes (back button, links) into the input.
  useEffect(() => {
    setSearchQuery(urlSearch);
  }, [urlSearch]);

  const statusChangeHandler = (_event: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (value === null) return;
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set('status', value);
    } else {
      next.delete('status');
    }
    setSearchParams(next, { replace: true });
  };

  // All groups (not just active): the survey list payload carries only
  // surveyQuestionGroupId, so the Question set column resolves names from this
  // list, and a survey can point at an archived group. The create dialog is
  // passed the active subset only.
  const loadGroups = useCallback(async () => {
    try {
      const data = await surveyAdminService.getGroups();
      setQuestionGroups(Array.isArray(data) ? data : (data as any).rows ?? []);
    } catch {
      setQuestionGroups([]);
    }
  }, []);

  // The survey list endpoint returns every row when page/perPage are omitted,
  // so fetch once and filter/paginate client-side.
  const loadSurveys = useCallback(async () => {
    setSurveys(undefined);
    try {
      const result = await surveyAdminService.getSurveys({ withCounts: true });
      setSurveys(result.rows);
    } catch {
      setSurveys([]);
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { loadSurveys(); }, [loadSurveys]);

  const normalisedSearch = debouncedSearchQuery.trim().toLowerCase();
  const filteredSurveys = (surveys ?? []).filter(s =>
    (!statusFilter || s.status === statusFilter) &&
    (!normalisedSearch || (s.name ?? '').toLowerCase().includes(normalisedSearch))
  );

  const groupNameById = new Map(questionGroups.map(g => [g.id, g.name]));

  const rows = filteredSurveys.map(s => ({
    id: s.id,
    name: s.name,
    type: s.purpose === 'SUPPLIER_PRODUCT_DATA'
      ? <Chip label='Product data request' size='small' variant='outlined' />
      : 'Question survey',
    status: <SurveyStatusChip status={s.status} />,
    responses: responsesCell(s),
    access: s.purpose === 'SUPPLIER_PRODUCT_DATA' ? '—' : (ACCESS_LABELS[s.distributionMode] ?? s.distributionMode),
    group: s.surveyQuestionGroup?.name
      ?? (s.surveyQuestionGroupId ? groupNameById.get(s.surveyQuestionGroupId) : undefined)
      ?? '—',
    created: moment(s.createdAt).format('DD/MM/YYYY'),
  }));

  return (
    <PageOuterContainer>
      <PageHeading
        title='Surveys'
        includeActionButton
        actionButtonText='New Survey'
        actionButtonIcon={IconName.Add}
        actionButtonVariant='contained'
        actionButtonHandler={() => setOpenDialog(true)}
      />
      {/* One toolbar row directly above the table: search on the left, the
          status filter it belongs with on the right. */}
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ width: { xs: '100%', sm: 320 } }}>
          <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} placeholder='Search surveys' />
        </Box>
        <ToggleButtonGroup
          size='small'
          exclusive
          value={statusFilter}
          onChange={statusChangeHandler}
          aria-label='Filter surveys by status'
          sx={segmentedToggleSx}
        >
          {STATUS_FILTERS.map(f => (
            <ToggleButton key={f.value} value={f.value} aria-label={f.ariaLabel}>{f.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      <LoadingPage pageReady={!!surveys}>
        <TableDisplay
          key={`${statusFilter}|${normalisedSearch}`}
          rows={rows}
          headCells={headCells}
          isMobileMode={isMobileMode}
          selected={selected}
          setSelected={setSelected}
          onRowClick={(id: string) => navigate(`/surveys/${id}`)}
        />
      </LoadingPage>
      <CreateSurveyDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        successHandler={loadSurveys}
        questionGroups={questionGroups.filter(g => g.active)}
      />
    </PageOuterContainer>
  );
};
